import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Circle as CircleIcon,
  Hand,
  Highlighter,
  Layers3,
  MessageCircle,
  PenLine,
  Pin,
  RectangleHorizontal,
  Redo2,
  Trash2,
  Undo2,
  ZoomIn,
  ZoomOut,
  type LucideIcon,
} from "lucide-react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { AppLink } from "@/components/app-link";
import { PilotChip } from "@/components/semantic-badges";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Toggle } from "@/components/ui/toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDocumentViewport } from "@/features/document-viewport/context";
import {
  clamp,
  ToolColorPicker,
  type Tool,
} from "@/features/rev-collab/rev-collab-session-helpers";
import type { PendingAnnotationDraft } from "@/features/rev-collab/rev-collab-types";
import { getViewportContentCenter } from "@/features/rev-collab/rev-collab-viewport-utils";
import { useCanEdit } from "@/lib/capabilities";

const TOOL_BUTTONS: Array<{ value: Tool; label: string; icon: LucideIcon }> = [
  { value: "select", label: "Select", icon: MessageCircle },
  { value: "hand", label: "Hand", icon: Hand },
  { value: "highlight", label: "Highlight", icon: Highlighter },
  { value: "rect", label: "Rect", icon: RectangleHorizontal },
  { value: "circle", label: "Circle", icon: CircleIcon },
  { value: "freehand", label: "Pen", icon: PenLine },
  { value: "pin", label: "Pin", icon: Pin },
];

interface RevCollabViewportOverlaysProps {
  tool: Tool;
  setTool: Dispatch<SetStateAction<Tool>>;
  toolColors: Record<Exclude<Tool, "select" | "hand">, string>;
  setToolColors: Dispatch<SetStateAction<Record<Exclude<Tool, "select" | "hand">, string>>>;
  canEditShapes: boolean;
  activePage: number;
  pageCount: number;
  takeoffLayerSummary: {
    jobId: Id<"takeoffPilotJobs">;
    jobName: string;
    detectionCount: number;
    measurementCount: number;
  } | null;
  showTakeoffLayer: boolean;
  onShowTakeoffLayerChange: (value: boolean) => void;
  undoCount: number;
  redoCount: number;
  canArchiveSelected: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onArchiveSelected: () => void;
  pendingAnnotationDraft: PendingAnnotationDraft | null;
  pendingCommentBody: string;
  setPendingCommentBody: Dispatch<SetStateAction<string>>;
  onClearPendingDraft: () => void;
  onSavePendingAnnotation: () => void;
  expandedThreadPanelWidth: number;
}

export function RevCollabViewportOverlays({
  tool,
  setTool,
  toolColors,
  setToolColors,
  canEditShapes,
  activePage,
  pageCount,
  takeoffLayerSummary,
  showTakeoffLayer,
  onShowTakeoffLayerChange,
  undoCount,
  redoCount,
  canArchiveSelected,
  onUndo,
  onRedo,
  onArchiveSelected,
  pendingAnnotationDraft,
  pendingCommentBody,
  setPendingCommentBody,
  onClearPendingDraft,
  onSavePendingAnnotation,
  expandedThreadPanelWidth,
}: RevCollabViewportOverlaysProps) {
  // Annotating is a project write; reviewers keep select, hand, page, and layer controls.
  const canEdit = useCanEdit();
  const viewport = useDocumentViewport();
  const contentCenter = useMemo(() => getViewportContentCenter(viewport), [viewport]);
  const takeoffLayerAvailable = Boolean(
    takeoffLayerSummary &&
      (takeoffLayerSummary.detectionCount > 0 || takeoffLayerSummary.measurementCount > 0)
  );

  const composerPosition = useMemo(() => {
    if (!pendingAnnotationDraft) {
      return null;
    }

    const composerWidth = Math.min(520, viewport.workspaceSize.width - 24);
    const composerHeight = 190;
    const leftLimit = 12;
    const rightLimit = Math.max(
      leftLimit,
      viewport.workspaceSize.width - expandedThreadPanelWidth - composerWidth - 12
    );
    const topLimit = 12;
    const bottomLimit = Math.max(topLimit, viewport.workspaceSize.height - composerHeight - 12);

    return {
      width: composerWidth,
      left: clamp(pendingAnnotationDraft.anchorScreenX + 12, leftLimit, rightLimit),
      top: clamp(pendingAnnotationDraft.anchorScreenY + 12, topLimit, bottomLimit),
    };
  }, [
    expandedThreadPanelWidth,
    pendingAnnotationDraft,
    viewport.workspaceSize.height,
    viewport.workspaceSize.width,
  ]);

  return (
    <>
      <div className="pointer-events-none absolute left-0 right-0 top-4 z-20 flex justify-center px-4">
        <div className="pointer-events-auto flex items-center gap-1 rounded-md border border-border/60 bg-background/90 px-1.5 py-1 shadow-sm backdrop-blur">
          <div className="flex items-center gap-1">
            {TOOL_BUTTONS.filter(
              (item) => canEdit || item.value === "select" || item.value === "hand"
            ).map((item) => (
              <IconButton
                key={item.value}
                label={item.label}
                icon={item.icon}
                variant="outline"
                pressed={tool === item.value}
                onClick={() => setTool(item.value)}
                disabled={!canEditShapes && item.value !== "select" && item.value !== "hand"}
              />
            ))}
            {tool !== "select" && tool !== "hand" ? (
              <>
                <div className="mx-1 h-4 w-px bg-border" />
                <ToolColorPicker
                  color={toolColors[tool]}
                  onChange={(color) =>
                    setToolColors((current) => ({ ...current, [tool]: color }))
                  }
                />
              </>
            ) : null}
          </div>

          <div className="mx-1.5 h-5 w-px bg-border" />

          <Tooltip>
            <TooltipTrigger asChild>
              {/* Disabled toggles swallow pointer events, so the wrapper owns the hover. */}
              <span className="inline-flex">
                <Toggle
                  variant="outline"
                  size="sm"
                  className="px-2 text-xs"
                  pressed={showTakeoffLayer && takeoffLayerAvailable}
                  onPressedChange={onShowTakeoffLayerChange}
                  disabled={!takeoffLayerAvailable}
                  aria-label="TakeoffPilot layer"
                >
                  <Layers3 />
                  <span className="hidden 2xl:inline">Takeoff</span>
                </Toggle>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {takeoffLayerAvailable
                ? `${showTakeoffLayer ? "Hide" : "Show"} read-only TakeoffPilot layer`
                : "No completed takeoff layer for this drawing page"}
            </TooltipContent>
          </Tooltip>

          {canEdit ? (
            <>
          <div className="mx-1.5 h-5 w-px bg-border" />

          <div className="flex items-center gap-1">
            <IconButton
              label="Undo"
              icon={Undo2}
              variant="outline"
              onClick={onUndo}
              disabled={undoCount === 0}
            />
            <IconButton
              label="Redo"
              icon={Redo2}
              variant="outline"
              onClick={onRedo}
              disabled={redoCount === 0}
            />
            <IconButton
              label="Delete annotation"
              icon={Trash2}
              variant="outline"
              onClick={onArchiveSelected}
              disabled={!canArchiveSelected}
            />
          </div>
            </>
          ) : null}

          <div className="mx-1.5 h-5 w-px bg-border" />

          <div className="flex items-center gap-1">
            <IconButton
              label="Previous page"
              icon={ChevronLeft}
              variant="outline"
              onClick={() => viewport.goToPage(activePage - 1)}
              disabled={activePage <= 1}
            />
            <div className="min-w-14 text-center text-xs tabular-nums text-muted-foreground">
              {activePage}/{pageCount}
            </div>
            <IconButton
              label="Next page"
              icon={ChevronRight}
              variant="outline"
              onClick={() => viewport.goToPage(activePage + 1)}
              disabled={activePage >= pageCount}
            />
          </div>

          <div className="mx-1.5 h-5 w-px bg-border" />

          <div className="flex items-center gap-1">
            <IconButton
              label="Zoom out"
              icon={ZoomOut}
              variant="outline"
              onClick={() => viewport.scaleZoomAt(0.9, contentCenter)}
            />
            <div className="min-w-12 text-center text-xs tabular-nums">
              {Math.round(viewport.camera.zoom * 100)}%
            </div>
            <IconButton
              label="Zoom in"
              icon={ZoomIn}
              variant="outline"
              onClick={() => viewport.scaleZoomAt(1.1, contentCenter)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="px-2"
              onClick={viewport.fitWidth}
            >
              Fit W
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="px-2"
              onClick={viewport.fitPage}
            >
              Fit P
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="px-2"
              onClick={() => viewport.requestZoomAt(1, contentCenter)}
            >
              100%
            </Button>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 hidden -translate-x-1/2 xl:block">
        <nav
          aria-label="Pilot glove"
          className="pointer-events-auto rounded-2xl border bg-background/95 p-2 shadow-xl backdrop-blur"
        >
          <div className="mb-1 px-2 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
            Pilot glove
          </div>
          <div className="flex items-center gap-1.5">
            <PilotChip pilot="spec" asChild>
              <AppLink to="/specPilot" title="SpecPilot">
                Spec
              </AppLink>
            </PilotChip>
            <PilotChip pilot="rev" asChild>
              <AppLink to="/revPilot" title="RevPilot">
                Rev
              </AppLink>
            </PilotChip>
            {takeoffLayerSummary ? (
              <PilotChip pilot="takeoff" asChild>
                <AppLink
                  to="/takeoffPilot/$jobId"
                  params={{ jobId: takeoffLayerSummary.jobId }}
                  title={`TakeoffPilot - ${takeoffLayerSummary.jobName}`}
                >
                  Takeoff
                </AppLink>
              </PilotChip>
            ) : (
              // No takeoff job on this drawing: the chip opens the TakeoffPilot list instead.
              <PilotChip pilot="takeoff" asChild className="opacity-70">
                <AppLink to="/takeoffPilot" title="TakeoffPilot">
                  Takeoff
                </AppLink>
              </PilotChip>
            )}
            <PilotChip pilot="collab" asChild>
              <AppLink to="/revCollab" title="CollabPilot">
                Collab
              </AppLink>
            </PilotChip>
          </div>
        </nav>
      </div>

      {pendingAnnotationDraft && composerPosition ? (
        <div className="pointer-events-none absolute inset-0 z-30">
          <Card
            className="pointer-events-auto absolute border border-border/70 bg-background/95 shadow-lg backdrop-blur"
            style={{
              width: composerPosition.width,
              left: composerPosition.left,
              top: composerPosition.top,
            }}
          >
            <CardContent className="space-y-2 p-2">
              <Label htmlFor="pending-annotation-comment" className="sr-only">
                Comment
              </Label>
              <Textarea
                id="pending-annotation-comment"
                autoFocus
                value={pendingCommentBody}
                onChange={(event) => setPendingCommentBody(event.target.value)}
                placeholder="Add comment..."
                rows={5}
                className="h-28 resize-y bg-transparent"
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && pendingCommentBody.trim()) {
                    event.preventDefault();
                    onSavePendingAnnotation();
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    onClearPendingDraft();
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="outline" onClick={onClearPendingDraft}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={onSavePendingAnnotation}
                  disabled={!pendingCommentBody.trim()}
                >
                  Save
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Cmd/Ctrl+Enter to save. Esc to cancel.
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {canEditShapes ? null : (
        <div className="pointer-events-none absolute bottom-4 left-4 z-20 rounded-md border bg-background/90 px-3 py-2 text-xs text-muted-foreground backdrop-blur">
          {canEdit
            ? "Mobile mode supports viewing and commenting. Annotation editing is desktop/tablet only."
            : "View only: browse annotations and their comments."}
        </div>
      )}
    </>
  );
}
